export interface PreferenceOption { id: string; label?: string; defaultKey?: string; archived: boolean; order: number; allowCustomValue?: boolean }
export interface PreferenceListChange { base: PreferenceOption[]; next: PreferenceOption[]; reset?: boolean }
export class PreferenceConflict extends Error { constructor() { super("PREFERENCE_LIST_CONFLICT"); } }

const fields = ["label", "defaultKey", "archived", "allowCustomValue"] as const;
export function normalizePreferenceList(input: unknown): PreferenceOption[] {
  if (!Array.isArray(input) || input.length > 500) throw new Error("INVALID_PREFERENCE_LIST");
  const ids = new Set<string>();
  return input.map((raw, index) => {
    if (!raw || typeof raw !== "object" || typeof raw.id !== "string" || !raw.id.trim() || raw.id.length > 120 || ids.has(raw.id)) throw new Error("INVALID_PREFERENCE_LIST");
    ids.add(raw.id);
    if (raw.label !== undefined && (typeof raw.label !== "string" || raw.label.length > 200)) throw new Error("INVALID_PREFERENCE_LIST");
    if (raw.defaultKey !== undefined && (typeof raw.defaultKey !== "string" || raw.defaultKey.length > 120)) throw new Error("INVALID_PREFERENCE_LIST");
    return { id: raw.id, ...(raw.label ? { label: raw.label.trim() } : {}), ...(raw.defaultKey ? { defaultKey: raw.defaultKey } : {}), archived: Boolean(raw.archived), allowCustomValue: Boolean(raw.allowCustomValue), order: Number.isFinite(raw.order) ? raw.order : index };
  }).sort((a,b)=>a.order-b.order).map((item,index)=>({...item,order:index}));
}

// Apply the authored differences to the latest list, preserving other devices' edits.
export function mergePreferenceList(currentInput: unknown, change: PreferenceListChange): PreferenceOption[] {
  if (!change || typeof change !== "object" || (change.reset !== undefined && typeof change.reset !== "boolean")) throw new Error("INVALID_PREFERENCE_LIST");
  const base = normalizePreferenceList(change.base), next = normalizePreferenceList(change.next);
  if (change.reset) return next;
  const current = normalizePreferenceList(currentInput === undefined ? base : currentInput);
  const baseById = new Map(base.map(row=>[row.id,row])), nextById = new Map(next.map(row=>[row.id,row]));
  const result = new Map(current.map(row=>[row.id,{...row}]));
  for (const previous of base) {
    if (nextById.has(previous.id)) continue;
    const latest = result.get(previous.id);
    if (latest && fields.some(field=>latest[field]!==previous[field])) throw new PreferenceConflict();
    result.delete(previous.id);
  }
  for (const desired of next) {
    const previous = baseById.get(desired.id), latest = result.get(desired.id);
    if (!previous) {
      if (latest && fields.some(field=>latest[field]!==desired[field])) throw new PreferenceConflict();
      if (!latest) result.set(desired.id,{...desired});
      continue;
    }
    const changed = fields.filter(field=>previous[field]!==desired[field]);
    if (!changed.length) continue;
    if (!latest) throw new PreferenceConflict();
    for (const field of changed) {
      if (latest[field]!==previous[field] && latest[field]!==desired[field]) throw new PreferenceConflict();
      if (desired[field] === undefined) delete (latest as unknown as Record<string, unknown>)[field];
      else (latest as unknown as Record<string, unknown>)[field] = desired[field];
    }
  }
  const previousOrder=JSON.stringify(base.filter(row=>nextById.has(row.id)).map(row=>row.id)), desiredOrder=JSON.stringify(next.filter(row=>baseById.has(row.id)).map(row=>row.id));
  const ids = previousOrder!==desiredOrder
    ? [...next.map(row=>row.id), ...current.map(row=>row.id).filter(id=>!nextById.has(id))]
    : [...current.map(row=>row.id), ...next.map(row=>row.id).filter(id=>!current.some(row=>row.id===id))];
  return [...new Set(ids)].filter(id=>result.has(id)).map((id,order)=>({...result.get(id)!,order}));
}
